// EditCourseDrawer.tsx
import { useEffect, useState } from 'react';
import { Drawer, Group, Button } from '@mantine/core';
import { toast } from '@/components/ui/providers/AlertProvider';
import { useACCourseService, ACCourse } from '@actschurch/ccms-sdk';
import CourseForm, { CourseFormState, EMPTY_COURSE_FORM } from './CourseForm';

interface EditCourseDrawerProps {
  opened: boolean;
  /**
   * `ACCourse` rather than `ACCourseModelView`: the drawer only reads the base fields, and the
   * course detail page holds the plain entity from `getById()`. An `ACCourseModelView` is still
   * accepted, since it carries every `ACCourse` field.
   */
  course: ACCourse | null;
  onClose: () => void;
  onUpdated: () => Promise<void> | void;
}

export default function EditCourseDrawer({ opened, course, onClose, onUpdated }: EditCourseDrawerProps) {
  const courseService = useACCourseService();
  const [form, setForm] = useState<CourseFormState>(EMPTY_COURSE_FORM);
  const [saving, setSaving] = useState(false);

  // Re-seed the form whenever a new course is passed in / the drawer opens
  useEffect(() => {
    if (!course) return;
    setForm({
      name: course.name ?? '',
      description: course.description ?? '',
      imageURL: course.imageURL ?? '',
      color: course.color ?? '',
      statusID: course.statusID ?? 0,
      isActive: course.isActive ?? course.statusID === 1,
    });
  }, [course]);

  const handleClose = () => {
    setForm(EMPTY_COURSE_FORM);
    onClose();
  };

  const handleSave = async () => {
    if (!course) return;
    if (!form.name?.trim()) {
      toast.error('Validation', 'Course name is required.');
      return;
    }
    setSaving(true);
    try {
      const { isActive, ...payload } = form;
      await courseService.update(course.courseID, payload as any);
      toast.success('Updated', 'Course updated successfully.');
      await onUpdated();
      handleClose();
    } catch (e) {
      console.error(e);
      toast.error('Error', 'Failed to update course.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer opened={opened} onClose={handleClose} title="Edit Course" position="right" size="md" padding="md">
      <CourseForm form={form} onChange={setForm} uploadRecordId={course ? String(course.courseID) : 'new'} />
      <Group justify="flex-end" mt="lg">
        <Button variant="subtle" onClick={handleClose}>Cancel</Button>
        <Button loading={saving} onClick={handleSave}>Update</Button>
      </Group>
    </Drawer>
  );
}